'use client';

import { MoreHorizontal, RotateCw, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { Handling } from '@/lib/services/matter-handling';
import { useInstitutionHandling } from './use-institution-handling';

/**
 * Hand-over, as a quiet secondary action in the institution sheet's HEADER
 * (EH-001, EH-005; moved here by VALE-1760).
 *
 * **Why the header and not the body.** It used to render as the first child of the
 * sheet body, directly above the Letter/Email composer, where it read as an
 * orphaned grey strip: handing a bank's work to the executor has nothing to do with
 * drafting a letter to that bank, and putting the two in one column implied it did.
 * The header is where the sheet's other whole-institution action already lives
 * (*Open conversation*), which is the right neighbourhood for it.
 *
 * **Renders in both states** (VALE-1802). It used to hide itself once the
 * institution was handed over, on the reasoning that {@link InstitutionHandedOverPanel}
 * already carries take-back and edit, so the menu had nothing left to offer. That
 * was a deviation from the ticket's own mock, flagged for review at the time and
 * never ruled on — and driving the feature settled it the other way: the header
 * `⋯` is where a reader has learned the sheet's whole-institution actions live, so
 * a header that silently loses its menu on some institutions and not others reads
 * as the sheet being broken, not as an absence of things to do.
 *
 * Handed over, it mirrors the panel rather than offering anything new. That
 * duplication is deliberate and cheap: the panel is the discoverable home for both
 * actions and this is the habitual one.
 *
 * `modal={false}` because the items open a `Dialog` — see `components/CLAUDE.md`
 * § "Dropdown that opens a modal Dialog/AlertDialog". Inside a Sheet the request is
 * ignored and the menu stays modal anyway (VALE-1418), which is the correct
 * behaviour here; the prop states the intent for the day this renders elsewhere.
 */
export function InstitutionHandlingMenu({
  matterId,
  institutionId,
  institutionName,
  handling
}: {
  matterId: number;
  institutionId: number;
  institutionName: string;
  handling: Handling | null | undefined;
}) {
  const { enabled, isHandled, isPending, openDialog, takeBack, dialog } =
    useInstitutionHandling({
      matterId,
      institutionId,
      institutionName,
      handling
    });

  if (!enabled) return null;

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="More institution actions"
            data-testid="institution-handling-menu-trigger"
          >
            <MoreHorizontal className="text-muted-foreground h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isHandled ? (
            <>
              <DropdownMenuItem
                data-testid="institution-handling-take-back"
                disabled={isPending}
                onClick={takeBack}
              >
                <RotateCw className="mr-2 h-4 w-4" />
                Take back into Vale
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="institution-handling-edit"
                disabled={isPending}
                onClick={openDialog}
              >
                <UserRound className="mr-2 h-4 w-4" />
                Edit handover details
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem
              data-testid="institution-handling-hand-over"
              disabled={isPending}
              onClick={openDialog}
            >
              <UserRound className="mr-2 h-4 w-4" />
              Hand over to someone else
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog}
    </>
  );
}
